import { ReactNode, useEffect } from 'react';
import Lenis from '@studio-freight/lenis';

interface LenisProviderProps {
  children: ReactNode;
}

export function LenisProvider({ children }: LenisProviderProps) {
  useEffect(() => {
    // Create Lenis instance with carefully tuned settings
    const lenis = new Lenis({
      // Core settings
      duration: 1.0,      // Default duration (not too fast, not too slow)
      smoothWheel: true,  // Enable smooth scrolling for mouse wheel

      // Advanced settings for more natural feel
      wheelMultiplier: 0.85,  // Slightly reduced multiplier for more control

      // Use a more natural easing curve - this is key for natural motion
      easing: (t) => {
        // Slight cubic ease-out for natural deceleration
        return 1 - Math.pow(1 - t, 3);
      },

      // Set orientation to vertical
      orientation: 'vertical',

      // Disable infinite scrolling
      infinite: false,
    });

    // Add event listeners to improve behavior
    lenis.on('scroll', (e: any) => {
      // You could track scroll position here for animations
      // This is useful for debugging
      // console.log('Scroll event:', e);
    });

    // The raf loop is critical for smooth performance
    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    // Start the loop
    requestAnimationFrame(raf);

    // Stop scrolling when window is resized (prevents stuttering)
    const resizeObserver = new ResizeObserver(() => {
      lenis.scrollTo(window.scrollY, { immediate: true });
    });

    resizeObserver.observe(document.body);

    // Custom scrollToSection implementation using Lenis
    window.scrollToSection = (sectionId: string) => {
      const section = document.getElementById(sectionId);
      if (section) {
        lenis.scrollTo(section, {
          offset: -50,  // Add some offset so it's not right at the top
          duration: 1.0, // Match our main duration
          immediate: false, // Use animation
        });
      }
    };

    // Clean up on unmount
    return () => {
      lenis.destroy();
      resizeObserver.disconnect();
      // @ts-ignore
      window.scrollToSection = undefined;
    };
  }, []);

  return <>{children} </>;
}

// Declare the scrollToSection function on the window object
declare global {
  interface Window {
    scrollToSection: (sectionId: string) => void;
  }
} 